// Lazy loadable modules mapped to selectors
const moduleMap: Record<string, () => Promise<any>> = {
  '.product': () => import('components/image-gallery'),
  '.content-swiper': () => import('components/content-swiper'),
  '.review-swiper': () => import('components/review-swiper'),
  '[data-lightbox]': () => import('components/lightbox'),
  '.pretty-check': () => import('components/pretty-check'),
  '.accordion': () => import('components/accordion'),
  '.dropdown-button': () => import('components/dropdown-button'),
  '.itinerary': () => import('components/itinerary'),
  '.currency-list__root': () => import('components/currency-list'),
  '.text-toggle': () => import('components/text-toggle'),
  '.mobile-navigation': () => import('components/mobile-navigation'),
  '.header-navigation': () => import('components/navigation'),
  '.accommodation-form-stopover__radio': () => import('components/update-accommodation-string'),
  '.masonry': () => import('libs/masonry'),
  '.calendar': () => import('components/calendar'),
  '#PickupLocation': () => import('components/select-pickup'),
  '.number-picker__input': () => import('components/number-picker'),
  '.product-template': () => import('components/product'),
  '.addtocart-form': async () => import('utility/add-to-cart'),
  '.doc-trcheckoutairportshuttleinformation': () => import('components/update-airport-fields'),
  '.trip-information-card': () => import('components/tripinformation'),
  '.share-buttons': () => import('components/share-buttons'),
  '.extra-service-wrapper, .extra-service-card': async () => {
    const removeExtraModule = await import('components/remove-extra')

    if (removeExtraModule.default && typeof removeExtraModule.default.init === 'function') {
      removeExtraModule.default.init()
    }

    return import('components/calculate-price')
  },
  '.price--universal': () => import('components/format-all-prices'),
  '.header': () => import('components/header'),
  '.tel-input': () => import('components/tel-input'),
  '.form': () => import('components/form'),
  '.checkout-form': () => {
    const cart = import('components/cart')
    cart.then((module) => module.default.init())

    return import('components/checkout/checkout')
  },
  '.extra-form': () => {
    // const cart = import('components/cart')
    // cart.then((module) => module.default.init())

    return import('components/checkout/checkout-extra')
  },
  '.region-filter': () => import('components/region-filter'),
  '.activity-list': () => import('components/activity-list'),
  '.modal': () => import('components/modals'),
  '.google-dropdown, .google-input': () => import('components/google-dropdown'),
  '.cart-floater': () => import('components/cart-floater-summary'),
  '.sidebar-cta': () => import('components/sidebar-calculate'),
  '.product-multi-day': () => import('components/sidebar-calculate-multiday'),
  '.static-header': () => import('components/static-header'),
  '.language-select': () => import('components/language-select'),
  '.datepicker': () => import('components/datepicker'),
  '.airport-shuttle-form__content': () => import('components/update-ratepriceid'),
  '.checkout-airport-shuttle-info': () => {
    const updateDateAirport = import('components/update-date-airport')
    updateDateAirport.then((module) => {
      if (module.default && typeof module.default.init === 'function') {
        module.default.init()
      }
    })
    return import('components/calculate-price-airport')
  },
  '.budget-select': () => import('components/budget-select'),
  '.receipt': () => import('components/checkout/receipt'),
  '.search-widget-input.dropdown-button': () => import('components/search-widget-calculate'),
  '.tour-dropdown': () => import('components/tour-dropdown'),
  '.drawer': () => import('components/drawer'),
  '.notification': () => import('components/notification'),
  '.search-results': () => import('components/search'),
  '.vvlazy': () => import('components/lazy-images'),

  // '.cart': () => import('components/cart'),
}

const eagerModules = ['.price--universal']

// Converts moduleMap into a Map for efficient lookups
const compiledModuleMap = new Map<string, () => Promise<any>>()
Object.entries(moduleMap).forEach(([selector, importer]) => {
  selector.split(',').forEach((subSelector) => {
    compiledModuleMap.set(subSelector.trim(), importer)
  })
})

// Preload timeout
function preloadTimeout() {
  const body = document.querySelector('.body')
  if (!body) {
    return
  }

  setTimeout(() => body.classList.add('loaded'), 100)
  setTimeout(() => {
    body.classList.add('preload--hidden')
    body.classList.remove('preload--transitions')
  }, 400)
}

// Add a simple guard for initCoreScripts to prevent double init
let _initCoreScriptsCalled = false

// Initialize core scripts
function initCoreScripts() {
  if (_initCoreScriptsCalled) {
    return
  }
  _initCoreScriptsCalled = true

  import('utility/prefetch').then((module) => module.default.init())
  import('utility/init-currency').then((module) => module.default())
  import('utility/toaster').then((module) => module.default.init())
  import('utility/htmx').then((module) => module.default.events())
  import('components/local-storage').then((module) => module.default.init())

  const header = document.querySelector('.header') as HTMLElement
  if (header) {
    // @ts-ignore
    window.htmx.process(header)
  }

  // Clickoutside logic initialization
  import('libs/clickoutside').then(async (module) => {
    return module.default(async () => {
      const [dropdownComponent, regionFilterComponent, languageSelectComponent, megamenu] = await Promise.all([
        import('components/dropdown-button').then((m) => m.default),
        import('components/region-filter').then((m) => m.default),
        import('components/language-select').then((m) => m.default),
        import('components/megamenu').then((m) => m.default),
      ])

      if (document.querySelector('.megamenu__inner') && megamenu) {
        megamenu.close()
      }

      document.querySelector('.google-dropdown')?.remove()

      const dropdown = document.querySelectorAll('.dropdown-button__body')
      if (dropdown) {
        dropdown.forEach((el) => {
          dropdownComponent.reset(el)
          const parent = el.parentNode as HTMLElement
          parent.classList.remove('dropdown-button--open', 'open')
        })
      }

      const datepicker = document.querySelectorAll('.datepicker__button')
      if (datepicker) {
        datepicker.forEach((el) => {
          if (el.classList.contains('datepicker__button--active')) {
            el.classList.remove('datepicker__button--active')
          }
        })
      }

      const regionFilter = document.querySelector('.region-filter__body.co-el')
      if (regionFilter?.classList.contains('open')) {
        regionFilterComponent.reset()
      }

      const cartOverview = document.querySelector('.cart-overview')
      if (cartOverview) {
        cartOverview.remove()
      }

      // const dropdownbuttonOpen = document.querySelector('.dropdown-button--open')
      // if (dropdownbuttonOpen) {
      //   dropdownbuttonOpen.classList.remove('dropdown-button--open')
      // }

      const drawer = document.querySelector('.drawer')
      if (drawer) {
        drawer.remove()
      }

      document.querySelectorAll('.language-list-wrapper.co-el').forEach((content) => {
        if (content.classList.contains('open')) {
          languageSelectComponent.closeDropdown(content as HTMLElement)
        }
      })
    })
  })

  if (document.querySelector('.megamenu')) {
    import('components/megamenu').then((module) => {
      module.default.reapplyState()
      module.default.init()
    })
  }
}

// Observe elements for lazy loading
function lazyLoadModules(root = document) {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const target = entry.target as HTMLElement
        const matchedImporters = Array.from(compiledModuleMap.entries())
          .filter(([selector]) => target.matches(selector))
          .map(([_, importer]) => importer)

        if (matchedImporters.length > 0) {
          Promise.allSettled(matchedImporters.map((importer) => importer()))
            .then((results) => {
              results.forEach((result) => {
                if (result.status === 'fulfilled' && result.value?.default?.init) {
                  result.value.default.init(target)
                }
              })
            })
            .catch(console.error)
        }

        observer.unobserve(target)
      }
    })
  })

  compiledModuleMap.forEach((importer, selector) => {
    const elements = root.querySelectorAll(selector)

    if (eagerModules.includes(selector)) {
      // Immediately load eager modules
      if (elements.length) {
        importer().then((mod) => {
          if (mod?.default?.init) {
            elements.forEach((el) => mod.default.init(el))
          }
        })
      }
    } else {
      elements.forEach((element) => observer.observe(element))
    }
  })
}

function refreshCartCount() {
  fetch('/umbraco/surface/renderview/cartcount')
    .then((res) => {
      if (!res.ok) {
        throw new Error('Failed to fetch cart count')
      }
      return res.text()
    })
    .then((html) => {
      document.querySelectorAll('.cart-button__counter__text').forEach((el) => {
        el.innerHTML = html
      })
    })
    .catch(console.error)
  // @ts-ignore
  // window.htmx.ajax('GET', '/umbraco/surface/renderview/cartcount', {
  //   target: '.cart-button__counter__text',
  //   swap: 'innerHTML',
  // })
}

document.body.addEventListener('htmx:afterSwap', (e: CustomEvent) => {
  const target = e?.detail?.target

  //  @ts-ignore
  window.htmx.process(target)

  const header = document.querySelector('.header') as HTMLElement
  if (header) {
    // @ts-ignore
    window.htmx.process(header)
  }

  if (e.detail.target && e.detail.target.id === 'content-container') {
    // You need to know the Model.Key client-side;
    // simplest is to echo it into a data-attr on body or the header once per page
    refreshCartCount()
  }

  // TODO: is initCoreScripts needed here?
  // initCoreScripts()
  lazyLoadModules()

  if (
    target?.classList &&
    ['accommodation-form-stopover__rooms__wrapper'].some((cls) => target?.classList?.contains(cls))
  ) {
    import('components/calculate-price').then((module) => module.default.updatePrices(e))
    return
  }
})

// Ensure full page reload behavior after HTMX swaps
document.body.addEventListener('htmx:afterSettle', () => {
  initCoreScripts()
  const header = document.querySelector('.header') as HTMLElement
  if (header) {
    // @ts-ignore
    window.htmx.process(header)
  }
  lazyLoadModules()
})

// Handle HTMX beforeSwap event for cleanup and smooth scrolling
document.body.addEventListener('htmx:beforeSwap', (e: CustomEvent) => {
  document.querySelectorAll('.button--loading')?.forEach((btn) => btn.classList.remove('button--loading'))

  const target = e?.detail?.target

  const skipScrollClasses = [
    'product-calendar__pickup',
    'product-calendar',
    'calendar__day',
    'select-departure',
    'dropdown-wrapper',
    'order-summary__section-item-wrapper',
    'airport-shuttle-form__timewrapper',
    'accommodation-rooms-wrapper',
    'static-header__cart-wrapper',
    'sidebar-cta__calendar',
  ]

  if (target && target.classList && skipScrollClasses.some((cls) => target.classList.contains(cls))) {
    return
  }

  if (
    e?.detail?.pathInfo?.requestPaths?.contains('modal') ||
    e?.detail?.pathInfo?.requestPath?.includes('modal') ||
    e?.detail?.pathInfo?.requestPath?.includes('accommodationdetail') ||
    e?.detail?.pathInfo?.requestPath?.includes('mainattractionsmobile') ||
    e?.detail?.pathInfo?.requestPath?.includes('itinerarymobile')
  ) {
    return
  }
  setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100)
})

window.addEventListener('popstate', () => {
  setTimeout(() => {
    initCoreScripts()
    lazyLoadModules()
    const pickuplocationSelect = document.querySelector('select#PickupLocation')

    if (pickuplocationSelect) {
      const addtocartButtons = document.querySelectorAll('.addtocart-button')
      addtocartButtons.forEach((button) => {
        if (!button.hasAttribute('disabled')) {
          button.setAttribute('disabled', '')
        }
      })
    }
  }, 0)
})

// window.addEventListener('pageshow', () => {
//   setTimeout(() => {
//     preloadTimeout()
//     initCoreScripts()
//     lazyLoadModules()
//     if (!document.querySelector('.addtocart-button')?.hasAttribute('disabled')) {
//       document.querySelector('.addtocart-button')?.setAttribute('disabled', '')
//     }
//   }, 0)
// })

// Load initial scripts and lazy load elements on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  preloadTimeout()
  initCoreScripts()
  lazyLoadModules()
})
